import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import backend from '../services/backend';

interface IUserData {
  id: number;
  name: string;
  email: string;
}

interface IAuthState {
  access_token: string;
  refresh_token: string;
  expires_in: string;
  user: IUserData;
}

interface ISignInCredentials {
  email: string;
  password: string;
}

type ResultSignIn = {
  message: string;
  usuario: {
    id: number;
    usuario: string;
    email: string;
  };
  permissoes: number[];
  token: {
    access_token: string;
    refresh_token: string;
    expires_in: number;
  };
};

interface IAuthContextData {
  user: IUserData;
  signIn(credentials: ISignInCredentials): Promise<ResultSignIn>;
  signOut(): void;
  setData(value: IAuthState | ((prev: IAuthState) => IAuthState)): void;
  permissoes: number[];
}

const AuthContext = createContext<IAuthContextData>({} as IAuthContextData);

const AuthProvider: React.FC = ({ children }) => {
  const [permissoes, setPermissoes] = useState<number[]>(() => {
    try {
      const perms = localStorage.getItem('@okton:permissoes');
      return perms ? JSON.parse(perms) : [];
    } catch (error) {
      localStorage.removeItem('@okton:permissoes');
      return [];
    }
  });
  const [data, setData] = useState<IAuthState>(() => {
    const access_token = localStorage.getItem('@okton:access_token');
    const refresh_token = localStorage.getItem('@okton:refresh_token');
    const expires_in = localStorage.getItem('@okton:expires_in');
    const user = localStorage.getItem('@okton:user');

    if (access_token && refresh_token && expires_in && user) {
      backend.defaults.headers.authorization = `Bearer ${access_token}`;
      try {
        const userParsed = typeof user === 'string' ? JSON.parse(user) : user;
        return { access_token, refresh_token, expires_in, user: userParsed };
      } catch (error) {
        console.error('Falha ao parsear os dados', error);
        return {} as IAuthState;
      }
    }

    return {} as IAuthState;
  });

  const signIn = useCallback(async ({ email, password }) => {
    try {
      const response = await backend.post<ResultSignIn>('/login', {
        email,
        senha: password,
      });
      const { usuario, token, permissoes } = response.data;

      const formatedUser = {
        id: usuario.id,
        name: usuario.usuario,
        email: usuario.email,
      };

      if (response.status === 200) {
        localStorage.setItem('@okton:access_token', token.access_token);
        localStorage.setItem('@okton:refresh_token', token.refresh_token);
        localStorage.setItem('@okton:expires_in', String(token.expires_in));
        localStorage.setItem('@okton:user', JSON.stringify(formatedUser));
        localStorage.setItem('@okton:permissoes', JSON.stringify(permissoes));

        backend.defaults.headers.authorization = `Bearer ${token.access_token}`;
        setPermissoes(permissoes);
        setData({
          access_token: token.access_token,
          refresh_token: token.refresh_token,
          expires_in: String(token.expires_in),
          user: formatedUser,
        });

        return {
          success: true,
          data: {},
          message: response.data.message,
        };
      }

      return {
        success: false,
        data: null,
        message: 'Falha no login: token não retornado.',
      };
    } catch (err) {
      return {
        success: false,
        data: [],
        message:
          err?.response?.data?.message ||
          err?.response?.data?.msg ||
          err?.message ||
          'Erro inesperado ao tentar login.',
      };
    }
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem('@okton:access_token');
    localStorage.removeItem('@okton:refresh_token');
    localStorage.removeItem('@okton:expires_in');
    localStorage.removeItem('@okton:user');

    setData({} as IAuthState);
  }, []);

  useEffect(() => {
    const interceptorId = backend.interceptors.response.use(
      response => response,
      error => {
        const status = error.response?.status;
        const message = error.response?.data?.message;

        if (status === 401 && message === 'Token inválido') {
          signOut();
        }

        return Promise.reject(error);
      },
    );

    return () => {
      backend.interceptors.response.eject(interceptorId);
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        signIn,
        signOut,
        permissoes,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): IAuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth deve ser usado com um AuthProvider');
  }

  return context;
}

export { AuthProvider, useAuth };
